import { IconNames } from '@dixa/lib-shared-habla';
import { SearchUserHit } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';

import { mediaToChannels } from 'utils/channels';

import { TeamChannelIcon } from '../../teamChannelIcon';

import { TooltipContent } from './tooltipContent';

type WorkloadConfig = NonNullable<SearchUserHit['workload_config']>;
type WorkloadConfigKey = keyof Omit<WorkloadConfig, 'id'>;

const iconsData: {
  icon: IconNames;
  workloadKeys: Set<Channel | 'unknown'>;
  workingMediaKey: string;
  limitKey: WorkloadConfigKey;
  tooltipText: string;
}[] = [
  {
    icon: 'phone',
    workloadKeys: new Set(['pstn_phone']),
    workingMediaKey: 'speak',
    limitKey: 'live_telephony_limit',
    tooltipText: 'conversations',
  },
  {
    icon: 'voicemail',
    workloadKeys: new Set(['voicemail', 'callback']),
    workingMediaKey: 'speak',
    limitKey: 'non_live_telephony_limit',
    tooltipText: 'voicemails',
  },
  {
    icon: 'mail',
    workloadKeys: new Set(mediaToChannels.email),
    workingMediaKey: 'email',
    limitKey: 'email_limit',
    tooltipText: 'conversations',
  },
  {
    icon: 'messaging-settings',
    workloadKeys: new Set(mediaToChannels.messaging),
    workingMediaKey: 'message',
    limitKey: 'messaging_limit',
    tooltipText: 'conversations',
  },
  {
    icon: 'chat',
    workloadKeys: new Set([...mediaToChannels.interactivechat, 'unknown']),
    workingMediaKey: 'interactive_chat',
    limitKey: 'interactive_chat_limit',
    tooltipText: 'chats',
  },
];

const conversationsByWorkload = (
  workloads: Partial<Record<Channel | 'unknown', number>>,
  workloadKeys: Set<Channel | 'unknown'>,
): number =>
  Object.keys(workloads).reduce(
    (acc, key) =>
      acc +
      (workloadKeys.has(key as Channel | 'unknown')
        ? workloads[key as Channel | 'unknown'] || 0
        : 0),
    0,
  );

type TeamChannelIconsListProps = {
  workloads_by_channel: SearchUserHit['workloads_by_channel'];
  working_media_types: SearchUserHit['working_media_types'];
  workload_config: SearchUserHit['workload_config'];
  status: 'working' | 'away' | 'offline';
  simultaneousRingEnabled: SearchUserHit['simultaneous_ring'];
};

export const TeamChannelIconsList = ({
  workloads_by_channel,
  working_media_types,
  workload_config,
  status,
  simultaneousRingEnabled,
}: TeamChannelIconsListProps) => {
  return (
    <>
      {iconsData.map((iconData) => {
        const count = workloads_by_channel
          ? conversationsByWorkload(workloads_by_channel, iconData.workloadKeys)
          : 0;
        const limit = workload_config?.[iconData.limitKey] ?? 0;

        return (
          <TeamChannelIcon
            key={iconData.icon}
            icon={iconData.icon}
            numberOfConversations={count}
            active={
              working_media_types?.includes(iconData.workingMediaKey) || false
            }
            status={status}
            tooltip={
              <TooltipContent
                conversationCount={count}
                conversationLimit={limit}
                text={iconData.tooltipText}
              />
            }
          />
        );
      })}
      <TeamChannelIcon
        icon="simultaneous"
        active={simultaneousRingEnabled || false}
        status={status}
      />
    </>
  );
};
